import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Link2, Copy, Sparkles, Share2, Mail, Check,
  FileText, AlignLeft, Loader2, ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const MODULE_LABELS = {
  friend: "A Friend's Wisdom", psychologist: "A Moment of Clarity",
  mentor: "Words of Guidance", social_guide: "Social Insight",
  job_guardian: "Career Wisdom", life_coach: "Life Lesson",
  storyteller: "A Story's Moral", philosopher_king: "Philosopher's Truth",
  creative_muse: "Creative Spark", executive_advisor: "Executive Insight",
};

const MODULE_GRADIENTS = {
  friend: "from-pink-500/20 via-rose-400/10 to-transparent",
  psychologist: "from-blue-500/20 via-sky-400/10 to-transparent",
  mentor: "from-amber-500/20 via-yellow-400/10 to-transparent",
  social_guide: "from-green-500/20 via-emerald-400/10 to-transparent",
  job_guardian: "from-purple-500/20 via-violet-400/10 to-transparent",
  life_coach: "from-orange-500/20 via-amber-400/10 to-transparent",
  storyteller: "from-teal-500/20 via-cyan-400/10 to-transparent",
};

const PLATFORMS = [
  { key: "twitter", label: "X / Twitter", icon: "𝕏", color: "bg-black text-white", hoverBg: "hover:bg-gray-900" },
  { key: "whatsapp", label: "WhatsApp", icon: null, color: "bg-[#25D366] text-white", hoverBg: "hover:bg-[#1fbd59]", svgPath: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" },
  { key: "facebook", label: "Facebook", icon: "f", color: "bg-[#1877F2] text-white", hoverBg: "hover:bg-[#1565c0]" },
  { key: "linkedin", label: "LinkedIn", icon: "in", color: "bg-[#0A66C2] text-white", hoverBg: "hover:bg-[#094d92]" },
  { key: "telegram", label: "Telegram", icon: null, color: "bg-[#0088cc] text-white", hoverBg: "hover:bg-[#0077b3]", svgPath: "M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" },
  { key: "email", label: "Email", icon: null, color: "bg-gray-600 text-white", hoverBg: "hover:bg-gray-700", lucide: Mail },
  { key: "copy", label: "Copy Link", icon: null, color: "bg-muted text-foreground", hoverBg: "hover:bg-muted/80", lucide: Link2 },
];

export function SocialShareModal({ content, module, wisdomFigure, conversationTitle, onClose, mode = "insight" }) {
  const { token } = useAuth();
  const [shareId, setShareId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [textMode, setTextMode] = useState("full"); // "full" | "summary"
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPlatforms, setShowPlatforms] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const label = MODULE_LABELS[module] || "Insight";
  const gradient = MODULE_GRADIENTS[module] || "from-primary/20 to-transparent";
  const displayContent = textMode === "summary" && summary ? summary : content;
  const cardContent = displayContent.length > 400 ? displayContent.slice(0, 397) + "..." : displayContent;

  // Generate AI summary on demand
  const generateSummary = async () => {
    if (summary) { setTextMode("summary"); return; }
    setLoadingSummary(true);
    try {
      const { data } = await axios.post(`${API}/share/generate-summary`, {
        content, module, wisdom_figure: wisdomFigure || null,
      }, { headers });
      setSummary(data.summary);
      setTextMode("summary");
    } catch (err) {
      toast.error("Failed to generate summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  // Create share link
  const ensureShareLink = async () => {
    if (shareId) return shareId;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/insights/share`, {
        message_content: cardContent, module,
      }, { headers });
      setShareId(data.id);
      setShowPlatforms(true);
      return data.id;
    } catch (err) {
      toast.error("Failed to create share link");
      return null;
    } finally {
      setCreating(false);
    }
  };

  const getShareUrl = (id) => `${window.location.origin}/api/share/${id}/card`;
  const getViewUrl = (id) => `${window.location.origin}/share/${id}`;

  const shareText = wisdomFigure
    ? `"${cardContent}"\n\n— ${wisdomFigure} (via Omni AI Wisdom Council)`
    : `"${cardContent}"\n\n— Shared from Omni, my AI companion`;

  const handlePlatformShare = async (platform) => {
    const id = await ensureShareLink();
    if (!id) return;
    const url = getShareUrl(id);
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(url);

    switch (platform) {
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, "_blank", "width=600,height=400");
        break;
      case "whatsapp":
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + "\n" + url)}`, "_blank");
        break;
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, "_blank", "width=600,height=400");
        break;
      case "linkedin":
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, "_blank", "width=600,height=400");
        break;
      case "telegram":
        window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, "_blank");
        break;
      case "email":
        window.location.href = `mailto:?subject=${encodeURIComponent(`${label} from Omni`)}&body=${encodeURIComponent(shareText + "\n\n" + url)}`;
        break;
      case "copy":
        navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Link copied!");
        setTimeout(() => setCopied(false), 2000);
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title: `${label} from Omni`, text: shareText, url });
          } catch (e) { /* cancelled */ }
        }
    }
  };

  const copyText = () => {
    navigator.clipboard.writeText(shareText);
    toast.success("Text copied to clipboard!");
  };

  return (
    <div className="fixed inset-0 z-[72] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <div className="flex justify-end mb-2">
          <button
            data-testid="share-modal-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/80 dark:bg-card/80 flex items-center justify-center hover:bg-white dark:hover:bg-card transition-colors shadow-sm"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Card Preview */}
        <div data-testid="share-preview-card" className={`bg-gradient-to-br ${gradient} bg-card border border-border/50 rounded-[2rem] p-7 shadow-xl`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
              <span className="text-xs font-semibold text-primary tracking-wide uppercase">{label}</span>
            </div>
            {wisdomFigure && (
              <span className="text-[10px] font-medium text-amber-600 bg-amber-400/10 px-2.5 py-1 rounded-full">
                {wisdomFigure}
              </span>
            )}
          </div>

          {/* Content */}
          <blockquote className="font-heading text-base sm:text-lg font-medium leading-relaxed text-foreground mb-6">
            &ldquo;{cardContent}&rdquo;
          </blockquote>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="text-xs font-heading font-medium">Omni</span>
            </div>
            {conversationTitle && (
              <span className="text-[10px] text-muted-foreground/60 truncate max-w-[140px]">
                {conversationTitle}
              </span>
            )}
          </div>
        </div>

        {/* Text Mode Toggle: Summary vs Full */}
        <div className="mt-4 flex items-center gap-2 p-1.5 bg-card border border-border/50 rounded-2xl">
          <button
            data-testid="share-mode-full"
            onClick={() => setTextMode("full")}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all",
              textMode === "full"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <FileText className="w-3.5 h-3.5" />
            Full Text
          </button>
          <button
            data-testid="share-mode-summary"
            onClick={generateSummary}
            disabled={loadingSummary}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all",
              textMode === "summary"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {loadingSummary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlignLeft className="w-3.5 h-3.5" />}
            {loadingSummary ? "Generating..." : "AI Summary"}
          </button>
        </div>

        {/* Actions */}
        <div className="mt-4 space-y-3">
          {/* Primary: Create Link & Share */}
          {!showPlatforms ? (
            <div className="space-y-2">
              <Button
                data-testid="create-share-btn"
                onClick={async () => { await ensureShareLink(); }}
                disabled={creating}
                className="w-full btn-omni bg-primary text-primary-foreground gap-2 h-12 text-sm"
              >
                {creating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Creating share link...</>
                ) : (
                  <><Share2 className="w-4 h-4" /> Share to Social Media</>
                )}
              </Button>
              <Button
                data-testid="copy-text-btn"
                onClick={copyText}
                variant="outline"
                className="w-full rounded-full gap-2 text-sm"
              >
                <Copy className="w-4 h-4" /> Copy Text
              </Button>
            </div>
          ) : (
            <>
              {/* Share URL */}
              <div className="flex items-center gap-2 p-3 bg-card border border-border/50 rounded-2xl">
                <input
                  data-testid="share-url-input"
                  value={shareId ? getShareUrl(shareId) : ""}
                  readOnly
                  className="flex-1 bg-transparent text-xs text-foreground truncate outline-none"
                  onClick={e => e.target.select()}
                />
                <button
                  data-testid="copy-share-url-btn"
                  onClick={() => handlePlatformShare("copy")}
                  className={cn(
                    "p-2 rounded-xl transition-all",
                    copied ? "bg-green-100 text-green-600" : "hover:bg-muted text-muted-foreground"
                  )}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              {/* Platform Grid */}
              <div className="grid grid-cols-4 gap-2">
                {PLATFORMS.map(p => {
                  const LucideIcon = p.lucide;
                  return (
                    <button
                      key={p.key}
                      data-testid={`share-${p.key}`}
                      onClick={() => handlePlatformShare(p.key)}
                      className={cn(
                        "flex flex-col items-center gap-1.5 p-3 rounded-2xl text-xs font-medium transition-all",
                        p.color, p.hoverBg
                      )}
                    >
                      {LucideIcon ? (
                        <LucideIcon className="w-5 h-5" strokeWidth={1.5} />
                      ) : p.icon ? (
                        <span className="text-base font-bold leading-none">{p.icon}</span>
                      ) : p.svgPath ? (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d={p.svgPath} /></svg>
                      ) : null}
                      <span className="text-[10px]">{p.label.split(" ")[0]}</span>
                    </button>
                  );
                })}
              </div>

              {/* Native share + copy text row */}
              <div className="flex gap-2">
                <Button
                  data-testid="copy-text-btn"
                  onClick={copyText}
                  variant="outline"
                  className="flex-1 rounded-full gap-2 text-xs"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy Text
                </Button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <Button
                    data-testid="native-share-btn"
                    onClick={() => handlePlatformShare("native")}
                    className="flex-1 btn-omni bg-primary text-primary-foreground gap-2 text-xs"
                  >
                    <Share2 className="w-3.5 h-3.5" /> More
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
